import React, { useState, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { ShieldCheckIcon, KeyIcon, QrCodeIcon, SpinnerIcon, CheckIcon, EyeIcon, EyeSlashIcon } from '../components/Icons';
import { useFlight } from '../contexts/FlightContext';

const MyAccountScreen: React.FC = () => {
    const { user, changeUserPassword, generateTwoFactorSecret, enableTwoFactor, disableTwoFactor } = useAuth();
    const { showNotification } = useFlight();

    // Password change state
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmNewPassword, setConfirmNewPassword] = useState('');
    const [isPasswordUpdating, setIsPasswordUpdating] = useState(false);
    const [showPasswords, setShowPasswords] = useState(false);

    // 2FA state
    const [twoFactorStep, setTwoFactorStep] = useState<'idle' | 'setup' | 'enabled'>('idle');
    const [qrCodeUrl, setQrCodeUrl] = useState('');
    const [secretKey, setSecretKey] = useState('');
    const [verificationCode, setVerificationCode] = useState('');
    const [recoveryCodes, setRecoveryCodes] = useState<string[]>([]);
    const [is2faLoading, setIs2faLoading] = useState(false);
    const [disablePassword, setDisablePassword] = useState('');

    const handlePasswordChange = async (e: React.FormEvent) => {
        e.preventDefault();
        if (newPassword.length < 6) {
            showNotification('New password must be at least 6 characters long.', 'error');
            return;
        }
        if (newPassword !== confirmNewPassword) {
            showNotification('New passwords do not match.', 'error');
            return;
        }
        setIsPasswordUpdating(true);
        const result = await changeUserPassword(currentPassword, newPassword);
        if (result.success) {
            showNotification('Password updated successfully!', 'success');
            setCurrentPassword('');
            setNewPassword('');
            setConfirmNewPassword('');
        } else {
            showNotification(result.message, 'error');
        }
        setIsPasswordUpdating(false);
    };

    const handleEnable2FA = async () => {
        setIs2faLoading(true);
        const result = await generateTwoFactorSecret();
        if (result) {
            setQrCodeUrl(result.qrCodeUrl);
            setSecretKey(result.secret);
            setTwoFactorStep('setup');
        } else {
            showNotification('Could not generate 2FA secret. Please try again.', 'error');
        }
        setIs2faLoading(false);
    };
    
    const handleVerifyAndEnable2FA = async () => {
        if (verificationCode.length !== 6) {
            showNotification('Please enter a valid 6-digit code.', 'error');
            return;
        }
        setIs2faLoading(true);
        const result = await enableTwoFactor(secretKey, verificationCode);
        if (result.success && result.recoveryCodes) {
            setRecoveryCodes(result.recoveryCodes);
            setTwoFactorStep('enabled');
            showNotification('2FA enabled successfully!', 'success');
        } else {
            showNotification(result.message || 'Verification failed. The code may be incorrect.', 'error');
        }
        setIs2faLoading(false);
    };

    const handleDisable2FA = async () => {
        setIs2faLoading(true);
        const result = await disableTwoFactor(disablePassword);
        if (result.success) {
            setTwoFactorStep('idle');
            setDisablePassword('');
            showNotification('2FA disabled successfully.', 'success');
        } else {
            showNotification(result.message, 'error');
        }
        setIs2faLoading(false);
    };

    const copyToClipboard = (text: string) => {
        navigator.clipboard.writeText(text).then(() => {
            showNotification('Copied to clipboard!', 'success');
        }).catch(() => {
            showNotification('Failed to copy.', 'error');
        });
    };
    
    if (!user) return null;

    return (
        <div className="max-w-4xl mx-auto space-y-8">
            <div className="text-center">
                <h1 className="text-3xl font-bold text-white">My Account</h1>
                <p className="text-slate-400 mt-1">Manage your password and account security settings.</p>
            </div>

            {/* Change Password Section */}
            <div className="bg-slate-900/60 backdrop-blur-md border border-slate-700 rounded-xl shadow-lg p-6">
                <h2 className="text-xl font-bold text-blue-400 mb-4 flex items-center gap-2"><KeyIcon className="w-6 h-6" /> Change Password</h2>
                <form onSubmit={handlePasswordChange} className="space-y-4 max-w-md">
                    <div className="relative">
                        <input type={showPasswords ? "text" : "password"} value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} placeholder="Current Password" required className="w-full pr-10" />
                        <button type="button" onClick={() => setShowPasswords(!showPasswords)} className="absolute inset-y-0 right-0 px-3 text-slate-400">
                           {showPasswords ? <EyeSlashIcon className="w-5 h-5"/> : <EyeIcon className="w-5 h-5"/>}
                        </button>
                    </div>
                    <input type={showPasswords ? "text" : "password"} value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="New Password" required className="w-full" />
                    <input type={showPasswords ? "text" : "password"} value={confirmNewPassword} onChange={(e) => setConfirmNewPassword(e.target.value)} placeholder="Confirm New Password" required className="w-full" />
                    <div>
                        <button type="submit" disabled={isPasswordUpdating} className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-lg disabled:bg-slate-500">
                            {isPasswordUpdating ? <SpinnerIcon className="w-5 h-5 animate-spin-slow"/> : 'Update Password'}
                        </button>
                    </div>
                </form>
            </div>
            
            {/* 2FA Section */}
            <div className="bg-slate-900/60 backdrop-blur-md border border-slate-700 rounded-xl shadow-lg p-6">
                <h2 className="text-xl font-bold text-fuchsia-400 mb-4 flex items-center gap-2"><ShieldCheckIcon className="w-6 h-6" /> Two-Factor Authentication (2FA)</h2>
                
                {user.twoFactorEnabled && twoFactorStep !== 'enabled' ? (
                     // 2FA IS ENABLED VIEW
                    <div>
                        <div className="bg-green-500/10 border border-green-500 text-green-300 p-4 rounded-lg flex items-center gap-3 mb-4">
                            <CheckIcon className="w-6 h-6"/>
                            <p className="font-semibold">Two-Factor Authentication is currently enabled on your account.</p>
                        </div>
                        <p className="text-slate-300 mb-4">To disable 2FA, please enter your current password for confirmation.</p>
                         <div className="flex items-stretch gap-2 max-w-md">
                            <input type="password" value={disablePassword} onChange={e => setDisablePassword(e.target.value)} placeholder="Your Password" className="flex-grow"/>
                            <button onClick={handleDisable2FA} disabled={is2faLoading} className="bg-red-600 hover:bg-red-700 text-white font-bold py-2 px-4 rounded-lg disabled:bg-slate-500">
                               {is2faLoading ? <SpinnerIcon className="w-5 h-5 animate-spin-slow"/> : 'Disable 2FA'}
                            </button>
                        </div>
                    </div>
                ) : twoFactorStep === 'idle' ? (
                    // 2FA IS DISABLED VIEW
                    <div>
                        <p className="text-slate-300 mb-4">Protect your account with an extra layer of security. When enabled, you'll need both your password and a code from an authenticator app to log in.</p>
                        <button onClick={handleEnable2FA} disabled={is2faLoading} className="bg-fuchsia-600 hover:bg-fuchsia-700 text-white font-bold py-2 px-4 rounded-lg disabled:bg-slate-500">
                            {is2faLoading ? <SpinnerIcon className="w-5 h-5 animate-spin-slow"/> : 'Enable Two-Factor Authentication'}
                        </button>
                    </div>
                ) : twoFactorStep === 'setup' ? (
                     // 2FA SETUP VIEW
                    <div className="space-y-6">
                        <div>
                            <p className="font-semibold text-white">1. Scan the QR code with your authenticator app</p>
                            <p className="text-sm text-slate-400 mb-4">Use an app like Google Authenticator, Microsoft Authenticator, or Authy.</p>
                            <div className="bg-white p-4 rounded-lg inline-block">
                                <img src={qrCodeUrl} alt="2FA QR Code" />
                            </div>
                        </div>
                        <div>
                             <p className="font-semibold text-white">Or, enter this key manually</p>
                             <div className="flex items-center gap-2 mt-2 bg-slate-700 p-2 rounded-lg max-w-xs">
                                <span className="text-amber-400 font-mono tracking-widest flex-grow">{secretKey}</span>
                                <button onClick={() => copyToClipboard(secretKey)} className="text-sm bg-slate-600 hover:bg-slate-500 px-3 py-1 rounded">Copy</button>
                             </div>
                        </div>
                         <div>
                            <p className="font-semibold text-white">2. Enter the 6-digit code from your app to verify</p>
                            <div className="flex items-stretch gap-2 max-w-md mt-2">
                                <input type="text" value={verificationCode} onChange={e => setVerificationCode(e.target.value)} placeholder="6-digit code" maxLength={6} inputMode="numeric" className="flex-grow"/>
                                <button onClick={handleVerifyAndEnable2FA} disabled={is2faLoading} className="bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-4 rounded-lg disabled:bg-slate-500">
                                   {is2faLoading ? <SpinnerIcon className="w-5 h-5 animate-spin-slow"/> : 'Verify & Enable'}
                                </button>
                            </div>
                        </div>
                    </div>
                ) : (
                    // 2FA ENABLED / RECOVERY CODES VIEW
                    <div className="space-y-4">
                         <div className="bg-green-500/10 border border-green-500 text-green-300 p-4 rounded-lg flex items-center gap-3">
                            <CheckIcon className="w-6 h-6"/>
                            <p className="font-semibold">2FA enabled successfully! Please save your recovery codes.</p>
                        </div>
                        <p className="text-amber-400 bg-amber-900/50 border border-amber-700 p-4 rounded-lg">
                            <strong className="block mb-2">Save these recovery codes!</strong>
                            If you lose access to your authenticator device, you will need one of these codes to log in. Each code can only be used once. Store them in a safe place.
                        </p>
                        <div className="grid grid-cols-2 gap-4 font-mono text-slate-300 bg-slate-700 p-4 rounded-lg">
                            {recoveryCodes.map(code => <span key={code}>{code}</span>)}
                        </div>
                        <button onClick={() => copyToClipboard(recoveryCodes.join('\n'))} className="bg-slate-600 hover:bg-slate-500 text-white font-bold py-2 px-4 rounded-lg">
                            Copy Codes
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default MyAccountScreen;